import type { AxiosError } from 'axios';
import axiosInstance from '../lib/axios';
import type {
  BillingHistoryItem,
  CurrentSubscription,
  Package,
  TokenPack,
  TokenPackListResult,
  TokenPackPurchaseResult,
} from '../types';
import type { ApiResponse } from './api';

type SubscriptionHistoryResponse = {
  subscriptions?: BackendSubscriptionHistoryItem[];
  billingItems?: BackendBillingHistoryItem[];
  total?: number;
  limit?: number;
  offset?: number;
};

type BackendPackagePrice = {
  currency: string;
  amountMinor: string | number;
  billingPeriod?: string;
  isActive?: boolean;
};

type BackendSubscriptionHistoryItem = {
  publicId: string;
  startAt: string;
  endAt: string;
  status: 'active' | 'trialing' | 'expired' | 'canceled' | string;
  package?: {
    name?: string;
    price?: number;
    prices?: BackendPackagePrice[];
  };
};

type BackendBillingHistoryItem = {
  publicId: string;
  type?: 'subscription' | 'token_pack';
  packageName?: string;
  itemName?: string;
  amount: number;
  currency?: string;
  status: BillingHistoryItem['status'];
  paymentDate: string;
  invoiceUrl?: string;
  createdAt: string;
};

type BackendTokenPackListResponse = {
  data?: TokenPack[] | TokenPackListResult;
};

const getErrorResponse = (
  error: unknown,
  fallback: string
): Pick<ApiResponse, 'success' | 'error' | 'errorCode' | 'message'> => {
  const axiosError = error as AxiosError<{ message?: string; error?: string }>;

  return {
    success: false,
    error: axiosError.response?.data?.error || axiosError.response?.data?.message || fallback,
    errorCode: axiosError.response?.data?.error,
    message: axiosError.response?.data?.message,
  };
};

const getPackagePriceAmount = (
  prices?: BackendPackagePrice[]
): { amount: number; currency?: string } => {
  const price = prices?.find((item) => item.currency === 'USD') || prices?.[0];

  if (!price) {
    return { amount: 0 };
  }

  const value = Number(price.amountMinor);

  return {
    amount: price.currency === 'USD' ? value / 100 : value,
    currency: price.currency,
  };
};

const mapBackendBillingHistoryItem = (item: BackendBillingHistoryItem): BillingHistoryItem => ({
  publicId: item.publicId,
  type: item.type,
  packageName: item.packageName || item.itemName || 'Unknown item',
  amount: item.amount,
  currency: item.currency,
  status: item.status,
  paymentDate: item.paymentDate,
  invoiceUrl: item.invoiceUrl,
  createdAt: item.createdAt,
});

const mapSubscriptionToBillingHistoryItem = (
  subscription: BackendSubscriptionHistoryItem | BillingHistoryItem
): BillingHistoryItem => {
  if ('packageName' in subscription) {
    return subscription;
  }

  const packagePrice = getPackagePriceAmount(subscription.package?.prices);

  return {
    publicId: subscription.publicId,
    packageName: subscription.package?.name || 'Unknown package',
    amount: subscription.package?.price ?? packagePrice.amount,
    currency: packagePrice.currency,
    status: subscription.status === 'trialing' ? 'PENDING' : 'PAID',
    paymentDate: subscription.startAt,
    createdAt: subscription.startAt,
  };
};

export const getPackages = async (): Promise<ApiResponse<Package[]>> => {
  try {
    const response = await axiosInstance.get('/v1/common/packages');

    return {
      success: true,
      data: response.data.data || response.data,
    };
  } catch (error) {
    console.error('Get packages error:', error);
    return getErrorResponse(error, 'Failed to load packages') as ApiResponse<Package[]>;
  }
};

export const getCurrentSubscription = async (): Promise<
  ApiResponse<CurrentSubscription | null>
> => {
  try {
    const response = await axiosInstance.get('/v1/subscriptions/current');

    return {
      success: true,
      data: response.data.data || response.data,
    };
  } catch (error) {
    console.error('Get current subscription error:', error);
    return getErrorResponse(
      error,
      'Failed to load current subscription'
    ) as ApiResponse<CurrentSubscription | null>;
  }
};

export const getBillingHistory = async (): Promise<ApiResponse<BillingHistoryItem[]>> => {
  try {
    const response = await axiosInstance.get('/v1/subscriptions/history');
    const payload = response.data.data || response.data;
    const billingItems = Array.isArray((payload as SubscriptionHistoryResponse).billingItems)
      ? ((payload as SubscriptionHistoryResponse).billingItems ?? [])
      : null;
    const subscriptions = Array.isArray(payload)
      ? payload
      : ((payload as SubscriptionHistoryResponse).subscriptions ?? []);

    return {
      success: true,
      data: billingItems
        ? billingItems.map(mapBackendBillingHistoryItem)
        : subscriptions.map(mapSubscriptionToBillingHistoryItem),
    };
  } catch (error) {
    console.error('Get billing history error:', error);
    return getErrorResponse(error, 'Failed to load billing history') as ApiResponse<
      BillingHistoryItem[]
    >;
  }
};

export const cancelSubscription = async (subscriptionPublicId: string): Promise<ApiResponse> => {
  try {
    const response = await axiosInstance.patch(
      `/v1/subscriptions/${encodeURIComponent(subscriptionPublicId)}/cancel`
    );

    return {
      success: true,
      data: response.data.data,
      message: 'Subscription cancelled successfully',
    };
  } catch (error) {
    console.error('Cancel subscription error:', error);
    return getErrorResponse(error, 'Failed to cancel subscription') as ApiResponse;
  }
};

export const resumeSubscription = async (subscriptionPublicId: string): Promise<ApiResponse> => {
  try {
    const response = await axiosInstance.patch(
      `/v1/subscriptions/${encodeURIComponent(subscriptionPublicId)}/resume`
    );

    return {
      success: true,
      data: response.data.data,
      message: response.data.message || 'Subscription resumed successfully',
    };
  } catch (error) {
    console.error('Resume subscription error:', error);
    return getErrorResponse(error, 'Failed to resume subscription') as ApiResponse;
  }
};

export const subscribe = async (packagePublicId: string): Promise<ApiResponse> => {
  try {
    const response = await axiosInstance.post('/v1/subscriptions/subscribe', {
      packagePublicId,
    });

    return {
      success: true,
      data: response.data.data,
      message: response.data.message || 'Subscription updated successfully',
    };
  } catch (error) {
    console.error('Subscribe error:', error);
    return getErrorResponse(error, 'Failed to process subscription') as ApiResponse;
  }
};

export const getTokenPacks = async (): Promise<ApiResponse<TokenPackListResult>> => {
  try {
    const response = await axiosInstance.get('/v1/subscriptions/token-packs');
    const responseBody = response.data as BackendTokenPackListResponse;
    const payload = responseBody.data || response.data;
    const tokenPacks = Array.isArray(payload)
      ? payload
      : ((payload as TokenPackListResult).tokenPacks ?? []);

    return {
      success: true,
      data: {
        tokenPacks,
      },
    };
  } catch (error) {
    console.error('Get token packs error:', error);
    return getErrorResponse(
      error,
      'Failed to load credit packs'
    ) as ApiResponse<TokenPackListResult>;
  }
};

export const purchaseTokenPack = async (
  tokenPackPublicId: string,
  currency?: string
): Promise<ApiResponse<TokenPackPurchaseResult>> => {
  try {
    const response = await axiosInstance.post(
      `/v1/subscriptions/token-packs/${tokenPackPublicId}/purchase`,
      currency ? { currency } : undefined
    );

    return {
      success: true,
      data: response.data.data || response.data,
      message: response.data.message || 'Credit pack purchased successfully',
    };
  } catch (error) {
    console.error('Purchase token pack error:', error);
    return getErrorResponse(
      error,
      'Failed to purchase credit pack'
    ) as ApiResponse<TokenPackPurchaseResult>;
  }
};

export const upgradeSubscription = subscribe;
